import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Badge,
  BarList,
  Button,
  Card,
  ChartCard,
  DeviceLink,
  EmptyState,
  KpiTile,
  RangePicker,
} from '@/components';
import type {
  AnalyticsSummary,
  BreakdownsResponse,
  GamesTrendResponse,
  Range,
  SubscriptionAnalytics,
  TimeseriesResponse,
  TopPlayersResponse,
} from '@/lib/admin-types';
import { PRIMARY_HEX } from '@/lib/colors';
import { formatInt } from '@/lib/format';
import { apiJson } from '@/lib/api';

type AdminSearch = { range: Range };

const OVERVIEW_HINTS = {
  kpiDau7:
    'Distinct devices with last_seen in the last 7 Pacific calendar days (including today; matches legacy dau_7d intent). For 30d/90d, the headline is the sum of per-day unique actives (same user active on multiple days can be counted more than once).',
  kpiDauSum:
    'Sum of daily unique active devices across each Pacific calendar day in the selected window. Not the same as unique users across the whole window—hover for 7d to use the deduplicated 7-day metric.',
  kpiUsersNotifOn:
    'Installations (anonymous devices) that have not turned off notifications (`unsubscribed_at` is empty). This is not the same as “can receive a push”—use the push-ready tiles for that. Delta compares current count to installs that already existed before this window with notifications still on.',
  kpiUsersPushMobile:
    'iOS or Android installs with a non-empty Expo push token and notifications still on—same rough audience as a broadcast to “all mobile” (see Engagement).',
  kpiUsersPushAll:
    'Any platform with a non-empty Expo push token and notifications still on.',
  kpiTotalUsers:
    'Registered installations (one `devices` row per anonymous install). Not the same as accounts.',
  kpiUsersNotifOff:
    'Installations that opted out of notifications (`unsubscribed_at` is set).',
  kpiRuns:
    'Total game runs (completed sessions) with played_at in the selected range (Pacific calendar days). Delta compares this window to the immediately previous window of equal length.',
  kpiPushes:
    'Count of engagement push send rows with sent_at in the selected range (Pacific calendar days; scheduled/reactivation traffic). No delta shown—use the chart for trends.',
  chartInstallsAccounts:
    'Pacific calendar days. Bars (left axis) are new installs: devices whose first_seen falls on that day. Line (right axis) is new signed-in accounts created that day. The series are not stacked. Daily actives are not on this chart — a device only stores its latest open, so grouping last_seen by day climbs toward today.',
  chartRunsPush:
    'Bars: game runs per Pacific day. Orange line: engagement pushes sent that day (right axis scale may differ).',
  topGames:
    'Games ranked by total runs in the selected range (from analytics/games-trend).',
  topPlayers:
    'Installations with the most runs in the selected range; nickname shown when the device registered one.',
  platformMix:
    'All registered devices grouped by client-reported platform (all-time snapshot, not filtered by the range picker).',
  quickActions:
    'Jump to push tools, learning content analytics, the merged activity feed, or (soon) export device rows for offline analysis.',
  subscriptionsActive:
    'Signed-in accounts with premium access (`subscribed` from RevenueCat or `subscription_override` for manual comps). Snapshot of active accounts, not devices.',
  subscriptionsRc:
    'Signed-in accounts where RevenueCat reports an active entitlement (`users.subscribed=true`). Under-counts anonymous buyers.',
  subscriptionsRcProfiles:
    'RevenueCat app_user profiles mirrored from webhooks/verify with an active entitlement — includes anonymous `$RCAnonymousID` buyers. Closest admin number to the RevenueCat “Active Subscriptions” dashboard.',
  subscriptionsRcAnonymous:
    'Subset of mirrored RC profiles that are still anonymous (never linked to a signed-in account).',
  subscriptionsOverride:
    'Manual comps only: override enabled without an active RevenueCat subscription.',
  subscriptionsRate:
    'Active subscribers divided by total signed-in accounts (non-deleted).',
  subscriptionsNewPurchases:
    'RC profiles (incl. anonymous) whose most recent webhook event in the window was INITIAL_PURCHASE (Pacific calendar days).',
  subscriptionsGrants:
    'RC profiles with any RevenueCat grant event in the window (purchase, renewal, uncancellation, etc.).',
  subscriptionsExpirations:
    'RC profiles whose most recent RevenueCat event in the window was EXPIRATION.',
  subscriptionsAutoRenew:
    'Among mirrored RC subscribers, count with auto-renew on vs off (from cached RC fields).',
  subscriptionsLifetime:
    'Mirrored RC subscribers with a lifetime (non-expiring) entitlement in the RevenueCat cache.',
  subscriptionsDonations:
    'Past donors who redeemed free subscription time (see Donations tab). All-time count.',
  subscriptionsByStore:
    'Mirrored RC subscribers grouped by RevenueCat store (App Store, Play Store, promotional, etc.).',
  subscriptionsByProduct:
    'Mirrored RC subscribers grouped by RevenueCat product identifier.',
} as const;

const STORE_LABELS: Record<string, string> = {
  app_store: 'App Store',
  mac_app_store: 'Mac App Store',
  play_store: 'Play Store',
  stripe: 'Stripe',
  promotional: 'Promotional',
};

function storeLabel(key: string): string {
  return STORE_LABELS[key] ?? key.replace(/_/g, ' ');
}

function formatSubscriberRate(rate: number): string {
  if (!Number.isFinite(rate)) return '—';
  return `${(rate * 100).toFixed(1)}%`;
}

function sumSeries(days: TimeseriesResponse['days'], key: keyof TimeseriesResponse['days'][0]) {
  return days.reduce((s, d) => s + (d[key] as number), 0);
}

function fmtDay(iso: string) {
  const [, m, day] = iso.split('-');
  return `${m}/${day}`;
}

function chartTooltipFormatter(value: unknown, name: unknown) {
  const v = typeof value === 'number' ? formatInt(value) : String(value);
  return [v, String(name)] as [string, string];
}

function AdminOverview() {
  const { range } = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/' });
  const qc = useQueryClient();

  const summaryQ = useQuery({
    queryKey: ['admin', 'summary', range],
    queryFn: () =>
      apiJson<AnalyticsSummary>(`/v1/admin/analytics/summary?range=${encodeURIComponent(range)}`),
  });

  const tsQ = useQuery({
    queryKey: ['admin', 'timeseries', range],
    queryFn: () =>
      apiJson<TimeseriesResponse>(
        `/v1/admin/analytics/timeseries?range=${encodeURIComponent(range)}`
      ),
  });

  const breakdownQ = useQuery({
    queryKey: ['admin', 'breakdowns'],
    queryFn: () => apiJson<BreakdownsResponse>('/v1/admin/analytics/breakdowns'),
  });

  const topPlayersQ = useQuery({
    queryKey: ['admin', 'top-players', range],
    queryFn: () =>
      apiJson<TopPlayersResponse>(
        `/v1/admin/analytics/top-players?range=${encodeURIComponent(range)}&limit=8`
      ),
  });

  const gamesTrendQ = useQuery({
    queryKey: ['admin', 'games-trend', range],
    queryFn: () =>
      apiJson<GamesTrendResponse>(
        `/v1/admin/analytics/games-trend?range=${encodeURIComponent(range)}`
      ),
  });

  const subscriptionsQ = useQuery({
    queryKey: ['admin', 'subscriptions', range],
    queryFn: () =>
      apiJson<SubscriptionAnalytics>(
        `/v1/admin/analytics/subscriptions?range=${encodeURIComponent(range)}`
      ),
  });

  const loading =
    summaryQ.isLoading ||
    tsQ.isLoading ||
    breakdownQ.isLoading ||
    topPlayersQ.isLoading ||
    gamesTrendQ.isLoading;

  const subscriptionsLoading = subscriptionsQ.isLoading;

  const err =
    summaryQ.error ||
    tsQ.error ||
    breakdownQ.error ||
    topPlayersQ.error ||
    gamesTrendQ.error;

  const subscriptionsErr = subscriptionsQ.error;

  const summary = summaryQ.data;
  const ts = tsQ.data;
  const breakdown = breakdownQ.data;
  const topPlayers = topPlayersQ.data;
  const gamesTrend = gamesTrendQ.data;
  const subscriptions = subscriptionsQ.data;

  const dauDisplay =
    range === '7d' && summary
      ? summary.dau_7d
      : ts
        ? sumSeries(ts.days, 'dau')
        : 0;

  const runsSum = ts ? sumSeries(ts.days, 'runs') : 0;
  const pushesSum = ts ? sumSeries(ts.days, 'pushes_sent') : 0;

  const chartRows =
    ts?.days.map((d) => ({
      ...d,
      label: fmtDay(d.date),
    })) ?? [];

  const topGameBars =
    gamesTrend?.games.slice(0, 8).map((g) => ({ key: g.display_name, count: g.total })) ?? [];

  const topPlayerBars =
    topPlayers?.by_runs.map((p) => ({
      key: p.installation_id,
      label: (
        <DeviceLink installationId={p.installation_id} nickname={p.nickname} />
      ),
      count: p.runs,
    })) ?? [];

  const subscriptionStoreBars =
    subscriptions?.by_store.map((entry) => ({
      key: storeLabel(entry.key),
      count: entry.count,
    })) ?? [];

  const subscriptionProductBars =
    subscriptions?.by_product.map((entry) => ({
      key: entry.key,
      count: entry.count,
    })) ?? [];

  const sparkRuns = ts?.days.map((d) => d.runs) ?? [];
  const sparkPushes = ts?.days.map((d) => d.pushes_sent) ?? [];

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Overview</h1>
          <p className="mt-1 text-sm text-gray-500">
            Operations snapshot — trends use the selected window (Pacific calendar days).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <RangePicker
            value={range}
            onChange={(r) => {
              void navigate({ search: { range: r } });
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              void qc.invalidateQueries({ queryKey: ['admin'] });
            }}
          >
            Refresh
          </Button>
          <Link
            to="/admin/engagement"
            className="text-sm font-semibold text-primary hover:underline"
          >
            Send test push →
          </Link>
        </div>
      </div>

      {err && (
        <p className="text-sm text-red-600">
          {(err as Error).message}
          <button
            type="button"
            className="ml-2 underline"
            onClick={() => void qc.invalidateQueries({ queryKey: ['admin'] })}
          >
            Retry
          </button>
        </p>
      )}

      {subscriptionsErr && (
        <p className="text-sm text-red-600">
          Subscriptions: {(subscriptionsErr as Error).message}
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label={range === '7d' ? 'DAU (7d unique)' : 'Σ daily active (approx.)'}
          value={dauDisplay}
          delta={summary?.delta_dau}
          loading={loading}
          tooltip={range === '7d' ? OVERVIEW_HINTS.kpiDau7 : OVERVIEW_HINTS.kpiDauSum}
        />
        <KpiTile
          label="Game runs"
          value={runsSum}
          delta={summary?.delta_runs}
          spark={sparkRuns.slice(-14)}
          loading={loading}
          tooltip={OVERVIEW_HINTS.kpiRuns}
        />
        <KpiTile
          label="Engagement pushes sent"
          value={pushesSum}
          spark={sparkPushes.slice(-14)}
          loading={loading}
          tooltip={OVERVIEW_HINTS.kpiPushes}
        />
        <KpiTile
          label="Total users"
          value={summary?.total_users ?? '—'}
          loading={loading}
          tooltip={OVERVIEW_HINTS.kpiTotalUsers}
        />
      </div>

      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-900">Notifications & push reach</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiTile
            label="Notifications on"
            value={summary?.users_notifications_on ?? '—'}
            delta={summary?.delta_users_notifications_on}
            loading={loading}
            tooltip={OVERVIEW_HINTS.kpiUsersNotifOn}
          />
          <KpiTile
            label="Notifications off"
            value={summary?.users_notifications_off ?? '—'}
            loading={loading}
            tooltip={OVERVIEW_HINTS.kpiUsersNotifOff}
          />
          <KpiTile
            label="Push-ready (iOS / Android)"
            value={summary?.users_push_mobile ?? '—'}
            loading={loading}
            tooltip={OVERVIEW_HINTS.kpiUsersPushMobile}
          />
          <KpiTile
            label="Push-ready (all platforms)"
            value={summary?.users_push_all ?? '—'}
            loading={loading}
            tooltip={OVERVIEW_HINTS.kpiUsersPushAll}
          />
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-gray-900">Subscriptions</h2>
          <Link
            to="/admin/donations"
            className="text-xs font-semibold text-primary hover:underline"
          >
            Donation rewards →
          </Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiTile
            label="RC profiles (incl. anon)"
            value={subscriptions?.rc_profile_subscribers ?? '—'}
            loading={subscriptionsLoading}
            tooltip={OVERVIEW_HINTS.subscriptionsRcProfiles}
          />
          <KpiTile
            label="RC anonymous only"
            value={subscriptions?.rc_anonymous_subscribers ?? '—'}
            loading={subscriptionsLoading}
            tooltip={OVERVIEW_HINTS.subscriptionsRcAnonymous}
          />
          <KpiTile
            label="Active account subscribers"
            value={subscriptions?.active_subscribers ?? '—'}
            loading={subscriptionsLoading}
            tooltip={OVERVIEW_HINTS.subscriptionsActive}
          />
          <KpiTile
            label="Signed-in RC subscribers"
            value={subscriptions?.rc_subscribers ?? '—'}
            loading={subscriptionsLoading}
            tooltip={OVERVIEW_HINTS.subscriptionsRc}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiTile
            label="Override-only comps"
            value={subscriptions?.override_only ?? '—'}
            loading={subscriptionsLoading}
            tooltip={OVERVIEW_HINTS.subscriptionsOverride}
          />
          <KpiTile
            label="Account subscriber rate"
            value={
              subscriptions
                ? formatSubscriberRate(subscriptions.subscriber_rate)
                : '—'
            }
            loading={subscriptionsLoading}
            tooltip={OVERVIEW_HINTS.subscriptionsRate}
          />
          <KpiTile
            label="New purchases"
            value={subscriptions?.new_purchases_in_window ?? '—'}
            delta={subscriptions?.delta_new_purchases}
            loading={subscriptionsLoading}
            tooltip={OVERVIEW_HINTS.subscriptionsNewPurchases}
          />
          <KpiTile
            label="Grant events"
            value={subscriptions?.grant_events_in_window ?? '—'}
            loading={subscriptionsLoading}
            tooltip={OVERVIEW_HINTS.subscriptionsGrants}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiTile
            label="Expirations"
            value={subscriptions?.expirations_in_window ?? '—'}
            loading={subscriptionsLoading}
            tooltip={OVERVIEW_HINTS.subscriptionsExpirations}
          />
          <KpiTile
            label="Signed-in accounts"
            value={subscriptions?.total_accounts ?? '—'}
            loading={subscriptionsLoading}
            tooltip="Registered Clerk accounts (non-deleted)."
          />
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <Card padding="md" className="cursor-help" title={OVERVIEW_HINTS.subscriptionsByStore}>
            <h3 className="mb-3 text-sm font-semibold text-gray-800">By store</h3>
            {subscriptionsLoading ? (
              <div className="h-40 animate-pulse rounded-xl bg-gray-200/60" />
            ) : (
              <BarList data={subscriptionStoreBars} emptyLabel="No store data" />
            )}
          </Card>
          <Card padding="md" className="cursor-help" title={OVERVIEW_HINTS.subscriptionsByProduct}>
            <h3 className="mb-3 text-sm font-semibold text-gray-800">By product</h3>
            {subscriptionsLoading ? (
              <div className="h-40 animate-pulse rounded-xl bg-gray-200/60" />
            ) : (
              <BarList data={subscriptionProductBars} emptyLabel="No product data" />
            )}
          </Card>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
            <KpiTile
              label="Auto-renew on"
              value={subscriptions?.auto_renew_on ?? '—'}
              loading={subscriptionsLoading}
              tooltip={OVERVIEW_HINTS.subscriptionsAutoRenew}
            />
            <KpiTile
              label="Auto-renew off"
              value={subscriptions?.auto_renew_off ?? '—'}
              loading={subscriptionsLoading}
              tooltip={OVERVIEW_HINTS.subscriptionsAutoRenew}
            />
            <KpiTile
              label="Lifetime entitlements"
              value={subscriptions?.lifetime_subscribers ?? '—'}
              loading={subscriptionsLoading}
              tooltip={OVERVIEW_HINTS.subscriptionsLifetime}
            />
            <KpiTile
              label="Donation rewards granted"
              value={subscriptions?.donation_rewards_granted ?? '—'}
              loading={subscriptionsLoading}
              tooltip={OVERVIEW_HINTS.subscriptionsDonations}
            />
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard
          title="New installs & accounts"
          tooltip={OVERVIEW_HINTS.chartInstallsAccounts}
          loading={loading}
          error={tsQ.error ? String(tsQ.error) : undefined}
          height={260}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis
                yAxisId="installs"
                domain={[0, 'auto']}
                allowDecimals={false}
                width={44}
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => formatInt(Number(v))}
              />
              <YAxis
                yAxisId="accounts"
                orientation="right"
                domain={[0, 'auto']}
                allowDecimals={false}
                width={36}
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => formatInt(Number(v))}
              />
              <Tooltip formatter={chartTooltipFormatter} />
              <Legend />
              <Bar
                yAxisId="installs"
                dataKey="registrations"
                name="New installs"
                fill={PRIMARY_HEX}
                radius={[4, 4, 0, 0]}
              />
              <Line
                yAxisId="accounts"
                type="monotone"
                dataKey="accounts"
                name="New accounts"
                stroke="#0ea5e9"
                strokeWidth={2}
                dot={chartRows.length <= 14}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Runs & pushes (daily)" tooltip={OVERVIEW_HINTS.chartRunsPush} loading={loading}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis
                yAxisId="left"
                allowDecimals={false}
                width={44}
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => formatInt(Number(v))}
              />
              <YAxis yAxisId="right" orientation="right" allowDecimals={false} width={36} hide />
              <Tooltip formatter={chartTooltipFormatter} />
              <Legend />
              <Bar yAxisId="left" dataKey="runs" name="Runs" fill={PRIMARY_HEX} radius={[4, 4, 0, 0]} />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="pushes_sent"
                name="Pushes"
                stroke="#f97316"
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card padding="md" className="cursor-help" title={OVERVIEW_HINTS.topGames}>
          <h2 className="mb-3 text-sm font-semibold text-gray-800">Top games</h2>
          {loading ? (
            <div className="h-40 animate-pulse rounded-xl bg-gray-200/60" />
          ) : topGameBars.length === 0 ? (
            <EmptyState title="No runs in window" />
          ) : (
            <BarList data={topGameBars} />
          )}
        </Card>
        <Card padding="md" className="cursor-help" title={OVERVIEW_HINTS.topPlayers}>
          <h2 className="mb-3 text-sm font-semibold text-gray-800">Top players (by runs)</h2>
          {loading ? (
            <div className="h-40 animate-pulse rounded-xl bg-gray-200/60" />
          ) : topPlayerBars.length === 0 ? (
            <EmptyState title="No players in window" />
          ) : (
            <BarList data={topPlayerBars} />
          )}
        </Card>
        <Card padding="md" className="cursor-help" title={OVERVIEW_HINTS.platformMix}>
          <h2 className="mb-3 text-sm font-semibold text-gray-800">Platform mix</h2>
          {loading ? (
            <div className="h-40 animate-pulse rounded-xl bg-gray-200/60" />
          ) : (
            <BarList data={breakdown?.by_platform ?? []} emptyLabel="No devices" />
          )}
        </Card>
      </div>

      <Card padding="lg" className="cursor-help" title={OVERVIEW_HINTS.quickActions}>
        <h2 className="mb-3 text-sm font-semibold text-gray-800">Quick actions</h2>
        <div className="flex flex-wrap gap-3">
          <Link to="/admin/engagement">
            <Button size="sm">Send test push</Button>
          </Link>
          <Link to="/admin/engagement">
            <Button variant="secondary" size="sm">
              Broadcast
            </Button>
          </Link>
          <Link to="/admin/activity">
            <Button variant="ghost" size="sm">
              Live activity
            </Button>
          </Link>
          <Link
            to="/admin/learning"
            search={{ range: '7d', page: 1, q: '', filter: 'all' }}
          >
            <Button variant="ghost" size="sm">
              Learning analytics
            </Button>
          </Link>
          <Badge variant="neutral">Export devices CSV — coming soon</Badge>
        </div>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/admin/')({
  validateSearch: (raw: Record<string, unknown>): AdminSearch => {
    const r = raw.range;
    if (r === '30d' || r === '90d' || r === '7d') return { range: r };
    return { range: '7d' };
  },
  component: AdminOverview,
});
